import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../utils/apiClient';
import { API_BASE_URL } from '../constants/api';
import '../styles/AdminPermissions.css';

const API_BASE = API_BASE_URL;

const PERMISSION_FIELDS = [
  { key: 'can_manage_projects', label: 'Proje Yönetimi' },
  { key: 'can_manage_tasks', label: 'Görev Yönetimi' },
  { key: 'can_manage_puantaj', label: 'Puantaj Giriş/Güncelleme' },
  { key: 'can_approve_puantaj', label: 'Puantaj Onaylama' },
  { key: 'can_manage_daily_reports', label: 'Günlük Rapor Yönetimi' },
  { key: 'can_manage_photos', label: 'Fotoğraf Yönetimi' },
  { key: 'can_manage_resources', label: 'Kaynak Yönetimi' },
  { key: 'can_manage_teams', label: 'Ekip Yönetimi' },
  { key: 'can_manage_users', label: 'Kullanıcı Yönetimi' },
  { key: 'can_manage_documents', label: 'Belge Yönetimi' },
  { key: 'can_manage_finance', label: 'Muhasebe Paneli' },
  { key: 'can_manage_change_orders', label: 'Değişiklik Emri Yönetimi' },
  { key: 'can_view_reports', label: 'Rapor Görüntüleme' },
];

const AdminPermissions = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState([]);

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  }, []);

  const isSuperAdmin = Number(currentUser?.id) === 1;

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`${API_BASE}/permissions`);
      setRows(Array.isArray(response.data) ? response.data : []);
      setError('');
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Yetki listesi yüklenemedi.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const filteredRows = rows.filter((row) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      String(row.username || '').toLowerCase().includes(q) ||
      String(row.email || '').toLowerCase().includes(q) ||
      String(row.role_name || '').toLowerCase().includes(q)
    );
  });

  const canEditRow = useCallback(
    (row) => !(Number(row.role_id) === 1 && !isSuperAdmin),
    [isSuperAdmin]
  );

  const editableFilteredRows = filteredRows.filter((row) => canEditRow(row));

  const areAllVisibleSelected =
    editableFilteredRows.length > 0 &&
    editableFilteredRows.every((row) => selectedUserIds.includes(Number(row.user_id)));

  const updatePermission = async (userId, key, value) => {
    const requestKey = `${userId}-${key}`;
    try {
      setSavingKey(requestKey);
      await apiClient.put(`${API_BASE}/permissions/${userId}`, { [key]: value });
      setRows((prev) => prev.map((row) => (row.user_id === userId ? { ...row, [key]: value } : row)));
      setError('');
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Yetki güncellenemedi.');
    } finally {
      setSavingKey('');
    }
  };

  const toggleRowSelection = (userId, checked) => {
    setSelectedUserIds((prev) => {
      const numericId = Number(userId);
      if (checked) {
        if (prev.includes(numericId)) return prev;
        return [...prev, numericId];
      }
      return prev.filter((id) => id !== numericId);
    });
  };

  const toggleSelectAllVisible = (checked) => {
    if (!checked) {
      const visibleIds = new Set(editableFilteredRows.map((row) => Number(row.user_id)));
      setSelectedUserIds((prev) => prev.filter((id) => !visibleIds.has(id)));
      return;
    }

    setSelectedUserIds((prev) => {
      const merged = new Set(prev);
      editableFilteredRows.forEach((row) => merged.add(Number(row.user_id)));
      return Array.from(merged);
    });
  };

  const bulkSetAllPermissions = async (enabled) => {
    const targetUserIds = selectedUserIds.filter((id) =>
      editableFilteredRows.some((row) => Number(row.user_id) === Number(id))
    );

    if (targetUserIds.length === 0) {
      setError('Toplu işlem için en az bir kullanıcı seçin.');
      return;
    }

    try {
      setSavingKey(`bulk-${enabled ? 'on' : 'off'}`);
      const payload = PERMISSION_FIELDS.reduce((acc, field) => {
        acc[field.key] = enabled;
        return acc;
      }, {});

      await Promise.all(targetUserIds.map((userId) => apiClient.put(`${API_BASE}/permissions/${userId}`, payload)));

      setRows((prev) =>
        prev.map((row) => (targetUserIds.includes(Number(row.user_id)) ? { ...row, ...payload } : row))
      );
      setError('');
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Toplu yetki güncelleme başarısız.');
    } finally {
      setSavingKey('');
    }
  };

  return (
    <div className="admin-permissions-page">
      <div className="admin-permissions-card">
        <div className="admin-permissions-head">
          <div className="admin-permissions-title">
            <h2>Admin Yetki Matrisi</h2>
            <small>Checkbox ile kişi bazlı özellik erişimi</small>
            {!isSuperAdmin && (
              <div className="admin-permissions-note">
                Not: Diğer admin hesaplarını sadece ana admin düzenleyebilir.
              </div>
            )}
          </div>

          <div className="admin-permissions-actions">
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Kullanıcı ara..."
              className="admin-search-input"
            />
            <button type="button" onClick={fetchRows} className="admin-btn admin-btn-secondary">
              Yenile
            </button>
            <button
              type="button"
              onClick={() => bulkSetAllPermissions(true)}
              disabled={savingKey.startsWith('bulk-') || selectedUserIds.length === 0}
              className="admin-btn admin-btn-success"
            >
              Seçilenlerde Tümünü Aç
            </button>
            <button
              type="button"
              onClick={() => bulkSetAllPermissions(false)}
              disabled={savingKey.startsWith('bulk-') || selectedUserIds.length === 0}
              className="admin-btn admin-btn-danger"
            >
              Seçilenlerde Tümünü Kapat
            </button>
          </div>
        </div>

        {error && <div className="admin-permissions-error">{error}</div>}

        {loading ? (
          <div className="admin-permissions-loading">Yükleniyor...</div>
        ) : (
          <div className="admin-permissions-table-wrap">
            <table className="admin-permissions-table">
              <thead>
                <tr>
                  <th className="admin-th admin-th-center">
                    <input
                      type="checkbox"
                      checked={areAllVisibleSelected}
                      onChange={(event) => toggleSelectAllVisible(event.target.checked)}
                      title="Görünenleri seç/bırak"
                    />
                  </th>
                  <th className="admin-th">Kullanıcı</th>
                  <th className="admin-th">Rol</th>
                  {PERMISSION_FIELDS.map((field) => (
                    <th key={field.key} className="admin-th">{field.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr key={row.user_id}>
                    <td className="admin-td admin-td-center">
                      <input
                        type="checkbox"
                        checked={selectedUserIds.includes(Number(row.user_id))}
                        onChange={(event) => toggleRowSelection(row.user_id, event.target.checked)}
                        disabled={!canEditRow(row)}
                      />
                    </td>
                    <td className="admin-td">
                      <strong>{row.username}</strong>
                      <div className="admin-email">{row.email}</div>
                    </td>
                    <td className="admin-td">{row.role_name || '-'}</td>
                    {PERMISSION_FIELDS.map((field) => {
                      const key = `${row.user_id}-${field.key}`;
                      return (
                        <td key={field.key} className="admin-td admin-td-center">
                          <input
                            type="checkbox"
                            checked={Boolean(row[field.key])}
                            disabled={savingKey === key || !canEditRow(row) || savingKey.startsWith('bulk-')}
                            onChange={(event) => updatePermission(row.user_id, field.key, event.target.checked)}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPermissions;
